import React from 'react';
import { Info } from 'lucide-react';
import {
  countCodePoints,
  PUSH_BODY_MAX_LENGTH,
  PUSH_IOS_PICTURE_NOTICE,
  PUSH_TITLE_MAX_LENGTH,
  validatePictureLink,
} from '../../utils/pushCampaignValidation';
import { PushPreview } from './PushPreview';

interface ContentStepProps {
  titleEn: string;
  bodyEn: string;
  titleFr: string;
  bodyFr: string;
  pictureLink: string;
  errors: string[];
  onTitleEnChange: (value: string) => void;
  onBodyEnChange: (value: string) => void;
  onTitleFrChange: (value: string) => void;
  onBodyFrChange: (value: string) => void;
  onPictureLinkChange: (value: string) => void;
}

export const ContentStep: React.FC<ContentStepProps> = props => {
  const pictureError = validatePictureLink(props.pictureLink);
  const safePictureLink = pictureError == null ? props.pictureLink.trim() : '';

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900">English</h3>
          {renderCharacterField({
            fieldKey: 'title-en',
            label: 'Title',
            value: props.titleEn,
            maxLength: PUSH_TITLE_MAX_LENGTH,
            multiline: false,
            onChange: props.onTitleEnChange,
          })}
          {renderCharacterField({
            fieldKey: 'body-en',
            label: 'Body',
            value: props.bodyEn,
            maxLength: PUSH_BODY_MAX_LENGTH,
            multiline: true,
            onChange: props.onBodyEnChange,
          })}
        </div>

        <div className="space-y-4 rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900">French</h3>
          <p className="text-xs text-gray-500">
            Optional: when left empty, French speaking recipients receive the English text.
          </p>
          {renderCharacterField({
            fieldKey: 'title-fr',
            label: 'Title',
            value: props.titleFr,
            maxLength: PUSH_TITLE_MAX_LENGTH,
            multiline: false,
            onChange: props.onTitleFrChange,
          })}
          {renderCharacterField({
            fieldKey: 'body-fr',
            label: 'Body',
            value: props.bodyFr,
            maxLength: PUSH_BODY_MAX_LENGTH,
            multiline: true,
            onChange: props.onBodyFrChange,
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Picture link</label>
        <input
          type="text"
          value={props.pictureLink}
          onChange={x => props.onPictureLinkChange(x.target.value)}
          placeholder="https://example.com/picture.jpg"
          className="mt-1 block w-full max-w-lg rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none"
        />
        <div className="mt-2 flex max-w-lg items-start gap-2 rounded-md bg-blue-50 p-3 text-xs text-blue-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{PUSH_IOS_PICTURE_NOTICE}</span>
        </div>
        {pictureError != null && (
          <p className="mt-1 text-sm text-red-700">{pictureError}</p>
        )}
        {safePictureLink !== '' && (
          <img
            src={safePictureLink}
            alt=""
            className="mt-2 max-h-40 rounded-lg border border-gray-200 object-cover"
          />
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <PushPreview label="English preview" title={props.titleEn} body={props.bodyEn} pictureLink={props.pictureLink} />
        <PushPreview
          label={props.titleFr.trim() === '' ? 'French preview, English fallback' : 'French preview'}
          title={props.titleFr.trim() === '' ? props.titleEn : props.titleFr}
          body={props.bodyFr.trim() === '' ? props.bodyEn : props.bodyFr}
          pictureLink={props.pictureLink}
        />
      </div>

      {props.errors.length > 0 && (
        <ul className="space-y-1 border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-700">
          {props.errors.map(x => <li key={x}>{x}</li>)}
        </ul>
      )}
    </div>
  );
};

function renderCharacterField(options: {
  fieldKey: string;
  label: string;
  value: string;
  maxLength: number;
  multiline: boolean;
  onChange: (value: string) => void;
}): React.ReactNode {
  const length = countCodePoints(options.value);
  const isOverLimit = length > options.maxLength;
  const fieldClassName = `mt-1 block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none ${
    isOverLimit ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-alps-primary'
  }`;

  return (
    <div key={options.fieldKey}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">{options.label}</label>
        <span className={`text-xs ${isOverLimit ? 'text-red-600' : 'text-gray-400'}`}>
          {length} / {options.maxLength}
        </span>
      </div>
      {options.multiline ? (
        <textarea
          value={options.value}
          rows={3}
          onChange={x => options.onChange(x.target.value)}
          className={fieldClassName}
        />
      ) : (
        <input
          type="text"
          value={options.value}
          onChange={x => options.onChange(x.target.value)}
          className={fieldClassName}
        />
      )}
    </div>
  );
}
